import { useState } from "react";
import { Lock, Info } from "lucide-react";
import { format, parseISO, isValid } from "date-fns";
import { AppAccessModal } from "./AppAccessModal";
import { ROLES } from "@/features/router/roles";

export function AppAccessBanner({
  role,
  access,
  supportPhone,
  isPaused,
  activeRestriction,
  districtRestriction,
  schoolRestriction,
}) {
  const [modalOpen, setModalOpen] = useState(false);

  const isStudent = role === ROLES.STUDENT;
  const activeEdRestriction = districtRestriction ?? schoolRestriction;
  const shouldShow = isStudent ? isPaused : !!activeEdRestriction;

  if (!shouldShow) return null;

  const dateSource = isStudent ? activeRestriction : activeEdRestriction;

  const resumesAt =
    dateSource?.resumesAt && isValid(parseISO(dateSource.resumesAt))
      ? parseISO(dateSource.resumesAt)
      : null;

  const resumeLabel = resumesAt
    ? format(resumesAt, "MMM d, yyyy")
    : "a future date";
  const sourceLabel = districtRestriction ? "District-wide" : "School-wide";

  const config = isStudent
    ? {
        wrapper: "bg-amber-50 border-b border-[#DDDDDD]",
        Icon: Lock,
        iconColor: "text-amber-500",
        titleColor: "text-amber-800",
        bodyColor: "text-amber-700",
        btnClass:
          "border border-[#DDDDDD] bg-white hover:bg-amber-50 text-amber-700",
        title: "App Access Paused",
        body: `Wellness practice is paused until ${resumeLabel}`,
        bodyShort: `Until ${resumeLabel}`,
      }
    : {
        wrapper: "bg-blue-50 border-b border-[#DDDDDD]",
        Icon: Info,
        iconColor: "text-[#3286DA]",
        titleColor: "text-[#1a5fa8]",
        bodyColor: "text-blue-700",
        btnClass:
          "border border-[#DDDDDD] bg-white hover:bg-blue-50 text-[#1a5fa8]",
        title: `App Temporarily Turned OFF for Students ${sourceLabel}`,
        body: `Students cannot access the app until ${resumeLabel}`,
        bodyShort: `Students paused until ${resumeLabel}`,
      };

  return (
    <>
      <div
        className={`sticky top-0 z-50 px-4 py-2.5 sm:px-8 ${config.wrapper}`}
      >
        <div className="flex items-center justify-between gap-3 max-w-5xl mx-auto">
          <div className="flex items-center gap-2 min-w-0">
            <config.Icon className={`w-4 h-4 shrink-0 ${config.iconColor}`} />
            <span className={`text-[16px] font-bold ${config.titleColor}`}>
              {config.title}
            </span>
            <span
              className={`text-[16px] opacity-75 hidden sm:inline ${config.bodyColor}`}
            >
              — {config.body}
            </span>
            <span
              className={`text-[14px] opacity-75 sm:hidden ${config.bodyColor}`}
            >
              {config.bodyShort}
            </span>
          </div>
          <button
            onClick={() => setModalOpen(true)}
            className={`shrink-0 text-[14px] font-bold px-3 py-1.5 rounded transition whitespace-nowrap ${config.btnClass}`}
          >
            View Details
          </button>
        </div>
      </div>

      <AppAccessModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        access={access}
        role={role}
        supportPhone={isStudent ? supportPhone : undefined}
        activeRestriction={activeRestriction}
        districtRestriction={districtRestriction}
        schoolRestriction={schoolRestriction}
      />
    </>
  );
}
